import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI
import { Autocomplete, CircularProgress, TextField } from '@mui/material';

// Services
import { getCompanies } from '../../../../services/companies.services';
import {
  deleteCompanySettingOverride,
  getCompanySettingOverrides,
  getOverridableSettings,
  upsertCompanySettingOverride,
} from '../../../../services/setting.service';

// Hooks
import { useNotifyMessage } from '../../../../hooks/useNotifyMessage';
import useConfirmDialog from '../../../../hooks/useConfirmDialog';

// Utils
import { makeCompanySettingColumns, MergedSettingRow } from '../../../../utils/columns/makeCompanySettingColumns';
import { getDataType, convertDataType } from '../../../../utils';

// Types
import { CONFIRM_DIALOG_CANCEL } from '../../../../constants/Constants';
import { Company } from '../../../../types/Company';
import { Setting } from '../../../../types/Settings';

// Common Components
import { useStandardTable as useTable, Table } from '../../../common/table';
import withForm from '../../../common/withForm';
import CustomModal from '../../../common/withModal';
import settingForm, { SettingInput } from '../settingForm';

// Styles
import './CompanySettings.scss';

const SettingForm = withForm(settingForm);

export default function CompanySettings() {
  const { notifyMessage } = useNotifyMessage();
  const { renderDialog, confirm } = useConfirmDialog(CONFIRM_DIALOG_CANCEL);

  // Company picker state
  const [companyOptions, setCompanyOptions] = useState<Company[]>([]);
  const [companyInputValue, setCompanyInputValue] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Bump this to force the table to re-fetch after a save / revert
  const [refreshTick, setRefreshTick] = useState(0);
  const bumpRefresh = () => setRefreshTick((n) => n + 1);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Load companies for the autocomplete
  useEffect(() => {
    setLoadingCompanies(true);
    getCompanies({ take: 999999, pageNumber: 1 })
      .then((res: any) => {
        const items = res?.data?.results ?? [];
        setCompanyOptions(items);
      })
      .finally(() => setLoadingCompanies(false));
  }, []);

  //  Table data source
  // Fetches overridable settings + this company's overrides in parallel,
  // then returns the merged rows in the shape useStandardTable expects.
  const fetchAsync = useCallback(async () => {
    if (!selectedCompany) {
      return { status: 200, data: { results: [], totalCount: 0 } } as any;
    }
    const [overridable, overrides] = await Promise.all([getOverridableSettings(), getCompanySettingOverrides(selectedCompany.id)]);

    const merged: MergedSettingRow[] = overridable.map((global) => ({
      global,
      override: overrides.find((o) => o.name === global.name && o.scope === global.scope),
    }));

    return { status: 200, data: { results: merged, totalCount: merged.length } } as any;
  }, [selectedCompany, refreshTick]);

  //  Row action handlers
  const handleEdit = ({ global, override }: MergedSettingRow) => {
    const current = override ?? global;
    const rawValue = current.value;
    formik.setValues({
      id: override?.id ?? '',
      name: global.name,
      scope: global.scope,
      dataType: getDataType(rawValue),
      value: typeof rawValue === 'object' && rawValue !== null ? JSON.stringify(rawValue, null, 2) : (rawValue as any),
    });
    setIsModalOpen(true);
  };

  const handleRevert = useCallback(
    async (override: Setting) => {
      const { accepted } = await confirm({});
      if (!accepted) return;
      await deleteCompanySettingOverride(selectedCompany!.id, override.id)
        .then(() => {
          notifyMessage('Override removed — reverted to global value', 'success');
          bumpRefresh();
        })
        .catch(() => {
          notifyMessage('Unable to remove override', 'error');
        });
    },
    [confirm, selectedCompany],
  );

  const columns = useMemo(() => makeCompanySettingColumns(handleEdit, handleRevert), [handleRevert]);

  //  Formik (edit / upsert override) --
  const formik = useFormik<SettingInput>({
    initialValues: {
      id: '',
      name: '',
      dataType: 'string',
      scope: '',
      value: '',
    },
    validationSchema: () =>
      Yup.object<SettingInput>({
        name: Yup.string().required('This field is required'),
        scope: Yup.string().required('This field is required'),
        dataType: Yup.string().required('This field is required'),
        value: Yup.string().required('This field is required'),
      }),
    enableReinitialize: true,
    onSubmit: async (values: SettingInput) => {
      const { name, scope, value, dataType } = values;
      const payload = {
        name,
        scope,
        value: convertDataType(value, dataType),
      };

      await upsertCompanySettingOverride(selectedCompany!.id, payload as any)
        .then(() => {
          notifyMessage('Company override saved!', 'success');
          setIsModalOpen(false);
          bumpRefresh();
        })
        .catch(() => {
          notifyMessage('Unable to save company override', 'error');
        });
    },
  });

  const params = useMemo(() => ({ companyId: selectedCompany?.id ?? null, refreshTick }), [selectedCompany?.id, refreshTick]);

  const table = useTable<MergedSettingRow>({
    columns,
    fetchAsync,
    params,
    serverSide: true,
    shouldFetch: !!selectedCompany,
  });

  return (
    <>
      {renderDialog()}

      <CustomModal
        title='Company Setting Override'
        isOpen={isModalOpen}
        isValid={formik.isValid}
        setIsOpen={setIsModalOpen}
        onConfirm={formik.handleSubmit}
      >
        <SettingForm formRef={formik} />
      </CustomModal>

      <div className='company-settings'>
        <div className='company-settings__container'>
          {/* Company picker */}
          <Autocomplete
            className='company-settings__picker'
            options={companyOptions}
            getOptionLabel={(option) => option.name}
            value={selectedCompany}
            onChange={(_, value) => setSelectedCompany(value)}
            inputValue={companyInputValue}
            onInputChange={(_, value) => setCompanyInputValue(value)}
            loading={loadingCompanies}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Select a company'
                variant='outlined'
                size='small'
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCompanies && <CircularProgress color='inherit' size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Settings table — only render once a company is picked */}
          {selectedCompany && (
            <Table
              {...table}
              showSearchBar={false}
              showColumnToggler={false}
              showAction={false}
              showPagination={true}
              minRows={0}
              getCoreRowModel={getCoreRowModel()}
            />
          )}
        </div>
      </div>
    </>
  );
}
